// src/pages/Products/OTAUpgrade.tsx

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Descriptions, Button, message, Alert, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { OTAUploadForm } from '@/components/forms/OTAUploadForm';
import { getOTA, uploadOTA } from '@/api/ota';
import { getCurrentDeveloperId } from '@/stores/authStore';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

/**
 * OTA 固件升级页面（覆盖旧版本）
 */
export const OTAUpgrade = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // 查询当前 OTA 固件信息
  const otaQuery = useQuery({
    queryKey: ['ota', developerId, productId],
    queryFn: () => {
      if (!productId) throw new Error('产品 ID 不能为空');
      return getOTA(productId);
    },
    enabled: !!productId && !!developerId,
  });

  const ota = otaQuery.data;
  const loading = otaQuery.isLoading;
  const error = otaQuery.error;

  /**
   * 处理固件升级
   */
  const handleUpgrade = async (version: string, description: string, file: File): Promise<void> => {
    if (!productId) throw new Error('产品 ID 不能为空');

    // 版本号比较（使用 ota?.version 直接比较）
    if (ota) {
      const [newMajor, newMinor, newPatch] = version.split('.').map(Number);
      const [currMajor, currMinor, currPatch] = ota.version.split('.').map(Number);

      if (newMajor < currMajor ||
          (newMajor === currMajor && newMinor < currMinor) ||
          (newMajor === currMajor && newMinor === currMinor && newPatch <= currPatch)) {
        message.error(`新版本号必须大于当前版本 v${ota.version}`);
        throw new Error('版本号必须递增');
      }
    }

    try {
      await uploadOTA(productId, { version, description }, file);
      message.success(`固件升级成功！新版本：v${version}`);

      // 无效化相关查询
      if (developerId) {
        queryClient.invalidateQueries({ queryKey: ['ota', developerId, productId] });
        queryClient.invalidateQueries({ queryKey: ['product', developerId, productId] });
      }

      // 跳转到 OTA 列表页
      navigate(`/products/${productId}/ota`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '升级失败，请检查参数后重试。';
      message.error(errorMessage);
      throw error;
    }
  };

  /**
   * 返回列表
   */
  const handleBack = () => {
    if (productId) {
      navigate(`/products/${productId}/ota`);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="加载失败" description="无法加载固件信息，请检查网络或稍后重试。" className="mb-8" />;
  if (!ota) {
    // 无固件，跳转到上传页面
    navigate(`/products/${productId}/ota/upload`);
    return <LoadingSpinner />;
  }

  // 格式化文件大小
  const formatFileSize = (size: number | null | undefined): string => {
    if (size == null) return '—';
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* ✅ 修改：标题 + 返回按钮在同一行，左右分布 */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">升级 OTA 固件</h1>
          <p className="text-gray-600">升级固件版本号并重新上传文件，升级后设备可获取新版本。</p>
        </div>
        <Button
          size="large"
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
          className="hover:bg-gray-50 transition-colors"
        >
          返回
        </Button>
      </div>

      {/* 当前固件信息卡片 */}
      <Card
        title="当前固件信息"
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 mb-8"
        style={{
          padding: '20px',
          '--ant-card-head-font-size': '16px',
          '--ant-card-head-font-weight': 600
        } as React.CSSProperties}
      >
        <Descriptions column={1} bordered={false} size="middle">
          <Descriptions.Item label="固件 ID" labelStyle={{ width: '140px', fontWeight: 600 }}>
            <span className="font-mono">{ota.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="当前版本" labelStyle={{ width: '140px', fontWeight: 600 }}>
            <Tag color="purple" className="text-sm px-3 py-1">v{ota.version}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="文件大小" labelStyle={{ width: '140px', fontWeight: 600 }}>
            {formatFileSize(ota.file_size)}
          </Descriptions.Item>
          <Descriptions.Item label="上传时间" labelStyle={{ width: '140px', fontWeight: 600 }}>
            {new Date(ota.created_at).toLocaleString('zh-CN')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <br />

      {/* 升级须知 */}
      <Alert
        message="升级须知"
        description={
          <>
            <p className="mb-2">1. <strong>新版本号必须严格大于当前版本号</strong>，例如从 v1.2.3 升级到 v1.2.4 或 v2.0.0</p>
            <p className="mb-2">2. <strong>文件大小限制 5MB</strong>，支持 .bin/.zip/.ota/.hex/.fw 格式</p>
            <p className="mb-2">3. <strong>升级后旧固件将被覆盖</strong>，所有设备将获取新版本</p>
            <p>4. <strong>删除操作不可恢复</strong>，请确认无设备依赖后再操作</p>
          </>
        }
        type="warning"
        showIcon
        className="mb-8 rounded-lg"
      />

      <br />

      {/* 升级表单 */}
      <OTAUploadForm onSubmit={handleUpgrade} currentVersion={ota.version} />
    </div>
  );
};